import { RequestServicesParams } from "@pages/offerView/types";
import {
  StepsBaseType,
  CreateOfferResponse,
  CreateOfferFieldsType,
  SingleServiceType,
  DisplayOfferFieldsType,
  CustomerType,
} from "../types";

const reverseEnum = (map: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(map).map(([key, value]) => [value, key]));

export const FLOW_OF_GOODS_MAP: Record<string, string> = {
  Import: "IMPORT",
  Export: "EXPORT",
};

export const MODE_OF_TRANSPORT_MAP: Record<string, string> = {
  "Maritime Transport": "MARITIME",
  "Land Transport": "LAND",
  "Air Transport": "AIR",
};

export const TYPE_OF_SHIPMENT_MAP: Record<string, string> = {
  Container: "CONTAINER",
  Bulk: "BULK",
};

export const REVERSE_FLOW_OF_GOODS_MAP = reverseEnum(FLOW_OF_GOODS_MAP);
export const REVERSE_MODE_OF_TRANSPORT_MAP = reverseEnum(MODE_OF_TRANSPORT_MAP);
export const REVERSE_TYPE_OF_SHIPMENT_MAP = reverseEnum(TYPE_OF_SHIPMENT_MAP);

export const normalizeOfferResponse = (
  offer: CreateOfferResponse,
): Partial<RequestServicesParams> => {
  const portsIds =
    offer.flow_of_goods === "IMPORT"
      ? [offer.discharge_port?.id as number]
      : [offer.loading_port?.id as number];
  return {
    flow_of_goods: offer.flow_of_goods || "",
    commodity__id__in: offer.commodities?.map((c) => c.id).join(",") ?? "",
    port__id__in: portsIds.join(","),
    incoterm: offer.incoterm === "CFR/CIF" ? "CFR_CIF" : offer.incoterm,
  };
};

export const normalizeOfferParams = (
  options: DisplayOfferFieldsType,
  createMode = false,
): CreateOfferFieldsType => {
  if (
    options &&
    options.flow_of_goods &&
    options.type_of_shipment &&
    options.mode_of_transport
  ) {
    const normalized = {
      ...options,
      flow_of_goods: FLOW_OF_GOODS_MAP[options.flow_of_goods] || "",
      mode_of_transport: MODE_OF_TRANSPORT_MAP[options.mode_of_transport] || "",
      type_of_shipment: TYPE_OF_SHIPMENT_MAP[options.type_of_shipment] || "",
      incoterm: options.incoterm === "CFR/CIF" ? "CFR_CIF" : options.incoterm,
      terminals: options.terminals?.map((item) => item.id) || [],
    };
    if (options.prices) {
      const services = options.prices?.map((service) => ({
        ...(!createMode ? { id: service?.association_id } : {}),
        active_price_id: (service.active_price as SingleServiceType)?.id,
        passive_price_ids:
          service.passive_prices?.map((passive) => passive.id) || [],
        prices: service.prices || [],
        expiration_date: (service.active_price as SingleServiceType)
          ?.expiration_date,
        comment: (service.active_price as SingleServiceType)?.comment,
      })) as any;
      normalized.prices = services;
    }
    return normalized as CreateOfferFieldsType;
  }
  return options as CreateOfferFieldsType;
};

export const updateStepsWithOfferData = (
  steps: StepsBaseType,
  offerData: CreateOfferResponse,
): StepsBaseType => {
  const newFields: CreateOfferFieldsType = {
    validity_start: offerData.validity_start,
    validity_end: offerData.validity_end,
    customer: offerData.customer as CustomerType & {
      terms_of_payment: {
        id: number;
        name: string;
      } | null;
    },
    customer_id: offerData.customer.id,
    operator_id: offerData.operator.id,
    terms_of_payment_id: offerData.terms_of_payment.id,
    terms_of_payment: offerData.terms_of_payment,
    terms_and_conditions: offerData.terms_and_conditions,
    comment: offerData.comment,
    object_it: offerData.object_it,
    object_eng: offerData.object_eng,
    incoterm: offerData.incoterm === "CFR_CIF" ? "CFR/CIF" : offerData.incoterm,
    terminals: offerData.terminals ?? null,
    commodities: offerData.commodities.map((c) => c.id),
    place_of_receipt_id: offerData.place_of_receipt?.id ?? null,
    place_of_delivery_id: offerData.place_of_delivery?.id ?? null,
    place_of_receipt: offerData.place_of_receipt,
    place_of_delivery: offerData.place_of_delivery,
    flow_of_goods: offerData.flow_of_goods
      ? REVERSE_FLOW_OF_GOODS_MAP[offerData.flow_of_goods]
      : null,
    loading_port: offerData.loading_port,
    loading_port_id: offerData.loading_port?.id || null,
    discharge_port: offerData.discharge_port,
    discharge_port_id: offerData.discharge_port.id,
    type_of_shipment: offerData.type_of_shipment
      ? REVERSE_TYPE_OF_SHIPMENT_MAP[offerData.type_of_shipment]
      : null,
    mode_of_transport: offerData.mode_of_transport
      ? REVERSE_MODE_OF_TRANSPORT_MAP[offerData.mode_of_transport]
      : null,
    assigned_office_id: offerData.assigned_office.id,
    assigned_office: offerData.assigned_office,
    container_shipment_type: offerData.container_shipment_type,
  };

  return steps.map((step) => {
    if (!step.fieldsValues) return step;

    const stepFieldsKeys = Object.keys(step.fieldsValues) as Array<
      keyof CreateOfferFieldsType
    >;

    const filteredFields = stepFieldsKeys.reduce((acc: any, key) => {
      if (key in newFields) {
        const value = newFields[key];
        acc[key] = value;
      }
      return acc;
    }, {} as Partial<CreateOfferFieldsType>);

    return {
      ...step,
      fieldsValues: {
        ...step.fieldsValues,
        ...filteredFields,
      },
    };
  });
};

export function flattenCommodities<T extends { id: number; children?: T[] }>(
  commodities: T[] = [],
): T[] {
  const result: T[] = [];

  for (const parent of commodities) {
    result.push(parent);

    if (parent.children?.length) {
      result.push(...parent.children);
    }
  }

  return result;
}

export function mapIdsToObjects<T extends { id: number }>(
  ids: number[] = [],
  items: T[] = [],
): T[] {
  const idsSet = new Set(ids);
  return items.filter((item) => idsSet.has(item.id));
}

export function getAgreementStyle(
  row: SingleServiceType,
  readonly: boolean,
  customerId?: number,
): React.CSSProperties {
  const style: React.CSSProperties = {
    backgroundColor: "inherit",
  };

  if (
    !readonly &&
    row?.expiration_date &&
    new Date(row.expiration_date) < new Date()
  ) {
    style.backgroundColor = "rgba(252, 228, 236, 1) !important";
  }

  if (
    customerId != null &&
    (row?.customers?.map((r) => r.id) || []).includes(customerId)
  ) {
    style.backgroundColor = "rgba(200, 230, 201, 1) !important";
  }

  return style;
}
